import { getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

export const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

// A manual selection becomes the new progress anchor. Small scrolls cannot
// undo it; a complete segment of intentional travel is needed to advance.
export function storyIndex(progress, count, current, anchor, hysteresis) {
  if (anchor) {
    const delta = progress - anchor.progress;
    const steps = Math.floor(Math.max(0, Math.abs(delta) - hysteresis) * count);
    return clamp(anchor.index + Math.sign(delta) * steps, 0, count - 1);
  }
  const raw = clamp(Math.floor(progress * count), 0, count - 1);
  if (raw > current && progress < (current + 1) / count + hysteresis) return current;
  if (raw < current && progress > current / count - hysteresis) return current;
  return raw;
}

export class SterlingProductScrollStory extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.section = this.closest('.shopify-section');
    this.desktop = matchMedia('(min-width: 1024px)');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.frame = null;
    this.anchor = null;
    this.programmatic = false;
    this.schedule = () => {
      if (this.frame !== null) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    };
    this.section.addEventListener('slideshow:select', (event) => {
      if (event.target !== this.slideshow || this.programmatic) return;
      if (event.detail?.userInitiated) this.setAnchor(event.detail.index);
    }, { signal });
    this.section.addEventListener('pointerdown', (event) => {
      if (event.target.closest('media-gallery')) this.setAnchor();
    }, { signal });
    window.addEventListener('resize', this.schedule, { signal });
    this.desktop.addEventListener('change', this.schedule, { signal });
    this.reducedMotion.addEventListener('change', this.schedule, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.schedule, { signal });
    this.observer = new ResizeObserver(this.schedule);
    this.observer.observe(this.section);
    customElements.whenDefined('slideshow-component').then(() => { if (this.isConnected) this.schedule(); });
    this.schedule();
  }

  setAnchor(index = this.slideshow?.current) {
    if (this.slideshow) this.anchor = { index, progress: this.progress ?? 0 };
  }

  update() {
    const target = getScrollEventTarget();
    if (target !== this.scrollTarget) {
      this.scrollTarget?.removeEventListener('scroll', this.schedule);
      this.scrollTarget = target;
      target.addEventListener('scroll', this.schedule, { passive: true });
    }
    const hero = this.section.querySelector(':is(.sterling-product, .sterling-standard-product) .product-information__grid');
    const media = hero?.querySelector('.product-information__media');
    const gallery = media?.querySelector('media-gallery');
    const slideshow = gallery?.querySelector('slideshow-component');
    if (slideshow !== this.slideshow) { this.slideshow = slideshow; this.anchor = null; }
    const count = slideshow?.slides?.length ?? 0;
    if (!hero || !media || !this.desktop.matches || this.reducedMotion.matches || count < 2 || typeof slideshow?.select !== 'function') {
      this.dataset.active = 'false';
      this.anchor = null;
      return;
    }
    const root = getScrollContainer();
    const rootTop = root === document.scrollingElement ? 0 : root.getBoundingClientRect().top;
    const top = parseFloat(getComputedStyle(media).top) || 0;
    const mediaHeight = media.getBoundingClientRect().height;
    const travel = hero.getBoundingClientRect().height - mediaHeight;
    const sticky = getComputedStyle(media).position === 'sticky';
    const active = sticky && travel >= Math.max(80, count * 24) && mediaHeight + top <= root.clientHeight + 2;
    this.dataset.active = String(active);
    if (!active) { this.anchor = null; return; }
    const distance = rootTop + top - hero.getBoundingClientRect().top;
    this.progress = clamp(distance / travel, 0, 1);
    // Outside the hero's sticky interval, leave the last/manual selection alone.
    if (distance < 0 || distance > travel) return;
    // Never interrupt a video or model that the shopper has started.
    const current = slideshow.slides[slideshow.current];
    if (!current) return;
    if ([...current.querySelectorAll('video')].some((video) => !video.paused) || current.querySelector('deferred-media[data-media-loaded], product-model[data-media-loaded]')?.isPlaying) {
      this.setAnchor();
      return;
    }
    const hysteresis = Math.min(.04, 8 / travel);
    const index = storyIndex(this.progress, count, slideshow.current, this.anchor, hysteresis);
    if (index === slideshow.current) return;
    this.programmatic = true;
    // Native selection synchronizes arrows, thumbnails and aria-selected. It
    // does not load deferred video/model content or invoke playback.
    slideshow.select(index, undefined, { animate: false });
    this.programmatic = false;
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.scrollTarget?.removeEventListener('scroll', this.schedule);
    if (this.frame !== null) cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('sterling-product-scroll-story')) customElements.define('sterling-product-scroll-story', SterlingProductScrollStory);
